import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { PriceChart } from '../PriceChart';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Calendar as CalendarIcon, Rocket, Maximize2, Minimize2 } from 'lucide-react';
import { formatNumber, formatSmallPrice, cn } from '../../lib/utils';

const formatTimePeriod = (hours) => {
  if (hours === 0.25) return '15m';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours === 168) return '7d';
  return `${hours}h`;
};

const HOUR_RANGES = [
  { value: 1, label: '1H' },
  { value: 6, label: '6H' },
  { value: 12, label: '12H' },
  { value: 24, label: '24H' },
  { value: 168, label: '7D' },
];

const CHART_PRESETS = [250, 350, 500, 700];

// Render detailed period stats (for 7d, 30d, 90d, last-24h)
const renderPeriodStats = (stats, label) => {
  if (!stats) return null;
  return (
    <div className="space-y-4 sm:space-y-6" data-testid="period-stats-content">
      <div className="p-3 sm:p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <p className="font-medium text-blue-700 text-sm sm:text-base">{label}</p>
        <p className="text-xs sm:text-sm text-muted-foreground">
          {stats.period?.start ? new Date(stats.period.start).toLocaleString() : 'N/A'}
          {' → '}
          {stats.period?.end ? new Date(stats.period.end).toLocaleString() : 'Now'}
        </p>
      </div>

      {/* ATH/ATL for period */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
          <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
            <p className="text-xs sm:text-sm text-muted-foreground">Period High</p>
          </div>
          <p className="text-base sm:text-xl font-bold text-green-600 truncate">
            {formatSmallPrice(stats.price?.ath)}
          </p>
          {stats.price?.ath_time && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {new Date(stats.price.ath_time).toLocaleString()}
            </p>
          )}
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20">
          <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <p className="text-xs sm:text-sm text-muted-foreground">Period Low</p>
          </div>
          <p className="text-base sm:text-xl font-bold text-red-600 truncate">
            {formatSmallPrice(stats.price?.atl)}
          </p>
          {stats.price?.atl_time && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {new Date(stats.price.atl_time).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Main metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Volume</p>
          <p className="text-base sm:text-xl font-bold text-emerald-600 truncate">
            {stats.totals?.volume_usd || '$0'}
          </p>
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Swaps</p>
          <p className="text-base sm:text-xl font-bold text-blue-600">
            {stats.totals?.swaps?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/20">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Price Change</p>
          <p className={`text-base sm:text-xl font-bold ${(stats.price?.change_percent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {stats.price?.change_percent !== null
              ? `${stats.price.change_percent >= 0 ? '+' : ''}${stats.price.change_percent.toFixed(2)}%`
              : 'N/A'
            }
          </p>
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Avg Price</p>
          <p className="text-base sm:text-xl font-bold text-amber-600 truncate">
            {formatSmallPrice(stats.price?.average)}
          </p>
        </div>
      </div>

      {/* Start/End prices */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="p-3 sm:p-4 rounded-lg border bg-muted/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 sm:mb-2">Start Price</p>
          <p className="font-mono text-sm sm:text-lg truncate">{formatSmallPrice(stats.price?.start)}</p>
        </div>
        <div className="p-3 sm:p-4 rounded-lg border bg-muted/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 sm:mb-2">End Price</p>
          <p className="font-mono text-sm sm:text-lg truncate">{formatSmallPrice(stats.price?.end)}</p>
        </div>
      </div>
    </div>
  );
};

export const MarketAnalyticsSection = ({
  selectedHours,
  onHoursSelect,
  hourlyLoading,
  hourlyStats,
  chartData,
  chartHeight,
  setChartHeight,
  chartLoading,
  selectedPreset,
  onPresetSelect,
  calendarOpen,
  setCalendarOpen,
  selectedDate,
  onDateSelect,
  dayStats,
  onSeeMoreDay,
  historicalLoading,
  last24hStats,
  periodStats,
  allTimeStats,
}) => (
  <Card data-testid="historical-stats">
    <CardHeader className="pb-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Market Analytics
        </CardTitle>
        {/* Time Range Selector */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          {HOUR_RANGES.map(({ value, label }) => (
            <Button
              key={value}
              variant={selectedHours === value ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 px-3 text-xs font-medium ${selectedHours === value ? '' : 'hover:bg-muted'}`}
              onClick={() => onHoursSelect(value)}
              disabled={hourlyLoading}
              data-testid={`time-range-${label.toLowerCase()}-button`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-2">
      {/* Stats Row - Volume, Swaps, Price Change, Current Price */}
      {hourlyStats && !hourlyLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
          <div className="p-2 sm:p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 min-w-0">
            <p className="text-xs text-muted-foreground mb-1 truncate">Vol ({formatTimePeriod(selectedHours)})</p>
            <p className="font-bold text-sm sm:text-lg text-emerald-600 truncate">{hourlyStats.totals?.volume_usd || '$0'}</p>
          </div>
          <div className="p-2 sm:p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 min-w-0">
            <p className="text-xs text-muted-foreground mb-1 truncate">Swaps ({formatTimePeriod(selectedHours)})</p>
            <p className="font-bold text-sm sm:text-lg text-blue-600">{hourlyStats.totals?.swaps?.toLocaleString() || 0}</p>
          </div>
          <div className="p-2 sm:p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 min-w-0">
            <p className="text-xs text-muted-foreground mb-1 truncate">Change ({formatTimePeriod(selectedHours)})</p>
            <p className={`font-bold text-sm sm:text-lg ${(hourlyStats.price?.change_percent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {hourlyStats.price?.change_percent !== null
                ? `${hourlyStats.price.change_percent >= 0 ? '+' : ''}${hourlyStats.price.change_percent.toFixed(2)}%`
                : 'N/A'
              }
            </p>
          </div>
          <div className="p-2 sm:p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 min-w-0">
            <p className="text-xs text-muted-foreground mb-1 truncate">Current Price</p>
            <p className="font-bold text-sm sm:text-lg text-amber-600 truncate">{formatSmallPrice(hourlyStats.price?.current || hourlyStats.price?.close)}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="p-3 rounded-lg bg-muted/30 border animate-pulse">
              <div className="h-3 bg-muted rounded w-16 mb-2"></div>
              <div className="h-5 bg-muted rounded w-20"></div>
            </div>
          ))}
        </div>
      )}

      {/* Price Chart - Resizable Container */}
      <div className="mb-4 p-4 rounded-lg border bg-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <h3 className="text-sm font-medium text-muted-foreground">Price & Volume</h3>
            {hourlyStats?.price && (
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-muted-foreground">High:</span>
                <span className="text-xs font-mono text-emerald-600">{formatSmallPrice(hourlyStats.price.high)}</span>
                <span className="text-xs text-muted-foreground">Low:</span>
                <span className="text-xs font-mono text-red-600">{formatSmallPrice(hourlyStats.price.low)}</span>
              </div>
            )}
          </div>
          {/* Chart Height Controls */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">Chart Height:</span>
            <div className="flex items-center gap-1 bg-muted/50 rounded-md p-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setChartHeight(Math.max(200, chartHeight - 50))}
                disabled={chartHeight <= 200}
                data-testid="chart-height-decrease-button"
              >
                <Minimize2 className="w-3 h-3" />
              </Button>
              <span className="text-xs font-mono w-12 text-center">{chartHeight}px</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setChartHeight(Math.min(800, chartHeight + 50))}
                disabled={chartHeight >= 800}
                data-testid="chart-height-increase-button"
              >
                <Maximize2 className="w-3 h-3" />
              </Button>
            </div>
            {/* Quick presets */}
            <div className="hidden md:flex items-center gap-1">
              {CHART_PRESETS.map((h) => (
                <Button
                  key={h}
                  variant={chartHeight === h ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setChartHeight(h)}
                  data-testid={`chart-height-preset-${h}-button`}
                >
                  {h === 250 ? 'S' : h === 350 ? 'M' : h === 500 ? 'L' : 'XL'}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <PriceChart
          data={chartData}
          height={chartHeight}
          showVolume={true}
          loading={chartLoading}
        />
      </div>

      {/* Period Preset Buttons */}
      <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
        <span className="text-sm text-muted-foreground mr-2 self-center">Historical:</span>
        <Button variant={selectedPreset === 'last-24h' ? 'default' : 'outline'} size="sm" onClick={() => onPresetSelect('last-24h')} data-testid="preset-last-24h-button">Last 24 Hours</Button>
        <Button variant={selectedPreset === '7d' ? 'default' : 'outline'} size="sm" onClick={() => onPresetSelect('7d')} data-testid="preset-7d-button">7 Days</Button>
        <Button variant={selectedPreset === '30d' ? 'default' : 'outline'} size="sm" onClick={() => onPresetSelect('30d')} data-testid="preset-30d-button">30 Days</Button>
        <Button variant={selectedPreset === '90d' ? 'default' : 'outline'} size="sm" onClick={() => onPresetSelect('90d')} data-testid="preset-90d-button">90 Days</Button>
        <Button variant={selectedPreset === 'all-time' ? 'default' : 'outline'} size="sm" onClick={() => onPresetSelect('all-time')} data-testid="preset-all-time-button">
          <Rocket className="w-4 h-4 mr-1" />All-Time
        </Button>
      </div>

      {/* Calendar Date Picker */}
      <div className="mb-6 p-4 rounded-lg bg-muted/30 border">
        <p className="text-sm font-medium mb-3 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" />
          Select Specific Date
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-[240px] justify-start text-left font-normal", !selectedDate && "text-muted-foreground")} data-testid="date-picker-button">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, 'PPP') : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={onDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {selectedDate && dayStats && !dayStats.error && (
            <Button variant="outline" size="sm" onClick={onSeeMoreDay} data-testid="see-more-btn">
              See Full Day View →
            </Button>
          )}
        </div>
      </div>

      {historicalLoading ? (
        <div className="text-center py-8">Loading historical stats...</div>
      ) : (
        <>
          {/* Last 24H Stats */}
          {selectedPreset === 'last-24h' && last24hStats && renderPeriodStats(last24hStats, 'Last 24 Hours (Rolling)')}

          {/* 7d, 30d, 90d Period Stats */}
          {selectedPreset === '7d' && periodStats && renderPeriodStats(periodStats, 'Last 7 Days')}
          {selectedPreset === '30d' && periodStats && renderPeriodStats(periodStats, 'Last 30 Days')}
          {selectedPreset === '90d' && periodStats && renderPeriodStats(periodStats, 'Last 90 Days')}

          {/* All-Time Stats */}
          {selectedPreset === 'all-time' && allTimeStats && !allTimeStats.error && (
            <div className="space-y-6" data-testid="all-time-stats-content">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <p className="text-sm text-muted-foreground">All-Time High (ATH)</p>
                  </div>
                  <p className="text-xl font-bold text-green-600">{formatSmallPrice(allTimeStats.ath?.price_usd)}</p>
                  {allTimeStats.ath?.time && <p className="text-xs text-muted-foreground mt-1">{new Date(allTimeStats.ath.time).toLocaleString()}</p>}
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-muted-foreground">All-Time Low (ATL)</p>
                  </div>
                  <p className="text-xl font-bold text-red-600">{formatSmallPrice(allTimeStats.atl?.price_usd)}</p>
                  {allTimeStats.atl?.time && <p className="text-xs text-muted-foreground mt-1">{new Date(allTimeStats.atl.time).toLocaleString()}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Rocket className="w-5 h-5 text-emerald-500" />
                    <p className="text-sm text-muted-foreground">Time Since Launch</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">{allTimeStats.hours_live ? `${Math.floor(allTimeStats.hours_live / 24)}d ${Math.floor(allTimeStats.hours_live % 24)}h` : 'N/A'}</p>
                  <p className="text-xs text-muted-foreground mt-1">Since: {allTimeStats.first_trade?.time ? new Date(allTimeStats.first_trade.time).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    <p className="text-sm text-muted-foreground">Price Change Since Launch</p>
                  </div>
                  <p className={`text-2xl font-bold ${(allTimeStats.price_change_since_launch || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {typeof allTimeStats.price_change_since_launch === 'number'
                      ? `${allTimeStats.price_change_since_launch >= 0 ? '+' : ''}${allTimeStats.price_change_since_launch.toFixed(2)}%`
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">First price: {formatSmallPrice(allTimeStats.first_trade?.price_usd)}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-violet-500" />
                    <p className="text-sm text-muted-foreground">Total Volume (All-Time)</p>
                  </div>
                  <p className="text-2xl font-bold text-violet-600">{allTimeStats.totals?.volume_usd || '$0'}</p>
                  <p className="text-xs text-muted-foreground mt-1">{allTimeStats.totals?.swaps?.toLocaleString() || 0} total swaps</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">At Launch</p>
                  <p className="font-mono text-lg">{formatSmallPrice(allTimeStats.first_trade?.price_usd)}</p>
                  <p className="text-xs text-muted-foreground mt-1">MCap: ${allTimeStats.first_trade?.market_cap ? formatNumber(allTimeStats.first_trade.market_cap.toFixed(0)) : 'N/A'}</p>
                </div>
                <div className="p-4 rounded-lg border bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Current</p>
                  <p className="font-mono text-lg">{formatSmallPrice(allTimeStats.current?.price_usd)}</p>
                  <p className="text-xs text-muted-foreground mt-1">MCap: ${allTimeStats.current?.market_cap ? formatNumber(allTimeStats.current.market_cap.toFixed(0)) : 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Day Stats from calendar */}
          {selectedPreset === 'custom' && dayStats && !dayStats.error && (
            <div className="space-y-6" data-testid="day-stats-content">
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="font-medium text-amber-700">Stats for {dayStats.date}</p>
                <p className="text-sm text-muted-foreground">{new Date(dayStats.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <p className="text-sm text-muted-foreground">Day High</p>
                  </div>
                  <p className="text-xl font-bold text-green-600">{formatSmallPrice(dayStats.price?.high)}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-muted-foreground">Day Low</p>
                  </div>
                  <p className="text-xl font-bold text-red-600">{formatSmallPrice(dayStats.price?.low)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
                  <p className="text-sm text-muted-foreground mb-2">Volume</p>
                  <p className="text-2xl font-bold text-emerald-600">{dayStats.totals?.volume_usd || '$0'}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <p className="text-sm text-muted-foreground mb-2">Swaps</p>
                  <p className="text-2xl font-bold text-blue-600">{dayStats.totals?.swaps?.toLocaleString() || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/20">
                  <p className="text-sm text-muted-foreground mb-2">Price Change</p>
                  <p className={`text-2xl font-bold ${(dayStats.price?.change_percent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {dayStats.price?.change_percent !== null ? `${dayStats.price.change_percent >= 0 ? '+' : ''}${dayStats.price.change_percent.toFixed(2)}%` : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Open Price</p>
                  <p className="font-mono text-lg">{formatSmallPrice(dayStats.price?.open)}</p>
                </div>
                <div className="p-4 rounded-lg border bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Close Price</p>
                  <p className="font-mono text-lg">{formatSmallPrice(dayStats.price?.close)}</p>
                </div>
              </div>
            </div>
          )}

          {selectedPreset === 'custom' && dayStats?.error && (
            <div className="text-center py-8 text-muted-foreground">No trading data for selected date.</div>
          )}
        </>
      )}
    </CardContent>
  </Card>
);
